"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Coffee, X, Check, Brain, ArrowRight } from "lucide-react";
import { startBreak, continueFocus, extendCurrentPhase, dismissPreBreak, type FocusSummary } from "@/lib/focus-timer-store";
import { useFocusState } from "@/components/focus/useFocusState";
import { formatDuration } from "@/lib/focus-plan";
import { playPreBreak, playSessionEnd } from "@/lib/focus-sounds";

// Delikatne, nieinwazyjne komunikaty: uprzedzenie o przerwie, wybór po sesji,
// podsumowanie sesji. Nie pojawiają się, gdy uczeń wyłączył automatyczne przerwy.
export default function FocusPrompts({ positionClass }: { positionClass: string }) {
  const reduce = useReducedMotion();
  const state = useFocusState();

  const showPreBreak = state.mode === "focus" && state.preBreakActive && state.autoBreaks;
  const showChoice = state.mode === "focus" && state.awaitingBreakChoice && state.autoBreaks;

  // Podsumowanie sesji — jednorazowe, po sygnale ze store'a.
  const [summary, setSummary] = useState<FocusSummary | null>(null);
  const lastSignal = useRef(0);
  useEffect(() => {
    if (state.sessionEndSignal > lastSignal.current) {
      lastSignal.current = state.sessionEndSignal;
      if (state.lastSummary) {
        setSummary(state.lastSummary);
        const t = setTimeout(() => setSummary(null), 7000);
        return () => clearTimeout(t);
      }
    }
  }, [state.sessionEndSignal, state.lastSummary]);

  // Dźwięki: uprzedzenie o przerwie i sygnał końca skupienia (raz na sesję).
  const preBreakSoundRef = useRef("");
  const choiceSoundRef = useRef("");
  useEffect(() => {
    if (showPreBreak && preBreakSoundRef.current !== state.id) {
      preBreakSoundRef.current = state.id;
      playPreBreak();
    }
  }, [showPreBreak, state.id]);
  useEffect(() => {
    if (showChoice && choiceSoundRef.current !== state.id) {
      choiceSoundRef.current = state.id;
      playSessionEnd();
    }
  }, [showChoice, state.id]);

  const anim = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 20, scale: 0.96 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 20, scale: 0.96 },
      };

  return (
    <div className={`fixed z-[70] ${positionClass}`}>
      <AnimatePresence mode="wait">
        {showChoice ? (
          <motion.div
            key="choice"
            {...anim}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            className="w-[20rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="flex items-center gap-2.5 bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 text-white">
              <Coffee size={18} />
              <p className="text-sm font-bold">Pora zrobić krótką przerwę?</p>
            </div>
            <div className="p-3">
              <p className="mb-3 px-1 text-xs text-slate-500 dark:text-slate-400">
                Zaplanowany czas skupienia dobiegł końca. Świetna robota!
              </p>
              <div className="grid gap-2">
                <button
                  onClick={() => startBreak()}
                  className="flex items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-dark"
                >
                  <Coffee size={15} /> Zrób przerwę
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => extendCurrentPhase(5)}
                    className="rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    Jeszcze 5 min
                  </button>
                  <button
                    onClick={continueFocus}
                    className="rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    Kontynuuj
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ) : summary ? (
          <motion.div
            key="summary"
            {...anim}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            className="w-[19rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="flex items-start gap-3 p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15">
                <Check size={20} strokeWidth={3} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-800 dark:text-white">Dobra robota!</p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Skupienie: {formatDuration(summary.focusedMs)}
                  {summary.breaksTaken > 0 && ` · Przerwy: ${summary.breaksTaken}`}
                </p>
                {summary.context?.subjectLabel && (
                  <p className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary">
                    <ArrowRight size={12} /> {summary.context.subjectLabel}
                  </p>
                )}
              </div>
              <button
                onClick={() => setSummary(null)}
                aria-label="Zamknij"
                className="rounded-lg p-1 text-slate-300 transition-colors hover:text-slate-500"
              >
                <X size={15} />
              </button>
            </div>
          </motion.div>
        ) : showPreBreak ? (
          <motion.div
            key="prebreak"
            {...anim}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            className="flex w-[18rem] max-w-[calc(100vw-2rem)] items-start gap-3 rounded-2xl border border-slate-200 bg-white/95 p-3.5 shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-500/15">
              <Brain size={17} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800 dark:text-white">Zostało Ci około 5 minut skupienia</p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Po tej sesji warto zrobić krótką przerwę ☕</p>
            </div>
            <button
              onClick={dismissPreBreak}
              aria-label="Zamknij"
              className="rounded-lg p-1 text-slate-300 transition-colors hover:text-slate-500"
            >
              <X size={15} />
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
